/**
 * Fix for Google Translate crash in React applications.
 *
 * When Chrome's built-in Google Translate or the Google Translate browser extension translates a page,
 * it mutates DOM text nodes or wraps them in <font> tags.
 *
 * When React updates or unmounts components, React expects DOM nodes to be direct children
 * of their original parent elements. When Google Translate alters this hierarchy, native DOM methods
 * like `removeChild` or `insertBefore` throw a NotFoundError DOMException.
 *
 * In React, an uncaught DOM exception during render/unmount unmounts the entire component tree,
 * resulting in a blank white screen.
 *
 * This utility safely monkey-patches `Node.prototype.removeChild` and `Node.prototype.insertBefore`
 * so parent-child mismatches caused by browser translation engines do not crash the React app.
 */

if (typeof Node !== 'undefined' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return child.parentNode.removeChild(child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (referenceNode.parentNode) {
        return referenceNode.parentNode.insertBefore(newNode, referenceNode);
      }
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}
